import {
  isFieldValueMissing,
  unwrapFieldValue,
  withFieldOrigin,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { ComputedSection } from 'features/libra/sections/computedSection/computedSectionTypes'
import reduce from 'lodash/reduce'

import { getComputeFunction } from './computeFunctionRegistry'

export interface ComputedSectionResult {
  computedResult: boolean | undefined
  isOverridden: boolean
  overrideValue: boolean | undefined
  finalResult: boolean | undefined
  overrideJustification: string | undefined
  overrideActor: string | undefined
  overrideTimestamp: string | undefined
  overrideParameterSnapshot: string | undefined
}

function hasParameterValue(
  config: ComputedSection,
  fieldValues: FieldValues
): boolean {
  return config.functionSpec.parameters.every((paramSpec) => {
    const value = unwrapFieldValue(fieldValues[paramSpec.field])
    return (
      (value !== undefined && value !== null && value !== '') ||
      paramSpec.defaultValue !== undefined
    )
  })
}

export function createParameterSnapshot(
  config: ComputedSection,
  fieldValues: FieldValues
): string {
  return JSON.stringify(
    config.functionSpec.parameters.map((paramSpec) => [
      paramSpec.field,
      unwrapFieldValue(fieldValues[paramSpec.field]) ?? null,
    ])
  )
}

export function getOverrideFieldIds(config: ComputedSection): string[] {
  return [
    config.overrideField,
    config.overrideJustificationField,
    config.overrideActorField,
    config.overrideTimestampField,
    config.overrideParameterSnapshotField,
  ].filter((fieldId): fieldId is string => fieldId !== undefined)
}

/**
 * Fields to null when dropping an override. A mapped `overrideField` is the
 * published enum (e.g. `eligibility`) and must be rewritten, not cleared.
 */
export function getOverrideFieldsToClear(config: ComputedSection): string[] {
  if (!config.overrideValueMap) return getOverrideFieldIds(config)
  return [
    config.overrideJustificationField,
    config.overrideActorField,
    config.overrideTimestampField,
    config.overrideParameterSnapshotField,
  ].filter((fieldId): fieldId is string => fieldId !== undefined)
}

function readStoredOverrideValue(
  config: ComputedSection,
  fieldValues: FieldValues
): boolean | undefined {
  if (!config.allowOverride || !config.overrideField) return undefined

  const raw = unwrapFieldValue(fieldValues[config.overrideField])
  const map = config.overrideValueMap
  if (map) {
    if (raw === map.true) return true
    if (raw === map.false) return false
    return undefined
  }

  return typeof raw === 'boolean' ? raw : undefined
}

export function serializeOverrideValue(
  config: ComputedSection,
  outcome: boolean
): boolean | string {
  const map = config.overrideValueMap
  return map ? (outcome ? map.true : map.false) : outcome
}

function readMappedOverrideSnapshot(
  config: ComputedSection,
  fieldValues: FieldValues
): string | undefined {
  if (!config.overrideParameterSnapshotField) return undefined
  const snapshot = unwrapFieldValue<string>(
    fieldValues[config.overrideParameterSnapshotField]
  )
  return typeof snapshot === 'string' ? snapshot : undefined
}

export function haveOverrideInputsChanged(
  config: ComputedSection,
  fieldValues: FieldValues,
  context?: Record<string, unknown>
): boolean {
  if (!config.allowOverride || !config.overrideField) return false

  const overrideValue = readStoredOverrideValue(config, fieldValues)
  if (overrideValue === undefined) return false

  const storedSnapshot = readMappedOverrideSnapshot(config, fieldValues)
  if (storedSnapshot !== undefined) {
    return storedSnapshot !== createParameterSnapshot(config, fieldValues)
  }

  if (config.overrideValueMap) return false

  const computedResult = evaluateComputedSection(config, fieldValues, context)
  return computedResult !== undefined && computedResult === overrideValue
}

function readLiveOverrideValue(
  config: ComputedSection,
  fieldValues: FieldValues,
  computedResult: boolean | undefined,
  context?: Record<string, unknown>
): boolean | undefined {
  if (haveOverrideInputsChanged(config, fieldValues, context)) return undefined

  const storedOverride = readStoredOverrideValue(config, fieldValues)
  if (storedOverride === undefined) return undefined

  if (config.overrideValueMap) {
    if (readMappedOverrideSnapshot(config, fieldValues) === undefined) {
      return undefined
    }
    if (computedResult !== undefined && storedOverride === computedResult) {
      return undefined
    }
  }

  return storedOverride
}

function writeMappedOverrideValue(
  config: ComputedSection,
  fieldValues: FieldValues,
  outcome: boolean
): FieldValues {
  if (!config.overrideValueMap || !config.overrideField) return fieldValues

  const published = serializeOverrideValue(config, outcome)
  if (unwrapFieldValue(fieldValues[config.overrideField]) === published) {
    return fieldValues
  }

  return {
    ...fieldValues,
    [config.overrideField]: wrapFieldValue(published),
  }
}

function persistMappedOverrideField(
  config: ComputedSection,
  fieldValues: FieldValues,
  finalResult: boolean | undefined
): FieldValues {
  if (finalResult === undefined) return fieldValues
  return writeMappedOverrideValue(config, fieldValues, finalResult)
}

function unpublishMappedOverrideField(
  config: ComputedSection,
  fieldValues: FieldValues
): FieldValues {
  if (!config.overrideValueMap || !config.overrideField) return fieldValues
  if (
    isFieldValueMissing(unwrapFieldValue(fieldValues[config.overrideField]))
  ) {
    return fieldValues
  }
  return {
    ...fieldValues,
    [config.overrideField]: wrapFieldValue(null),
  }
}

/**
 * Re-applies a live mapped override after branch.setFields. Leftover enums
 * without a snapshot are not overrides and must not win over setFields.
 */
export function restoreMappedOverrideField(
  config: ComputedSection,
  published: FieldValues,
  target: FieldValues,
  context?: Record<string, unknown>
): FieldValues {
  if (!config.overrideValueMap || !config.overrideField) return target
  if (
    !evaluateComputedSectionWithOverride(config, published, context)
      .isOverridden
  ) {
    return target
  }
  const publishedValue = published[config.overrideField]
  if (publishedValue === undefined) return target
  if (
    unwrapFieldValue(target[config.overrideField]) ===
    unwrapFieldValue(publishedValue)
  ) {
    return target
  }
  return { ...target, [config.overrideField]: publishedValue }
}

function stripInactiveMappedOverrideMetadata(
  config: ComputedSection,
  fieldValues: FieldValues,
  isOverridden: boolean
): FieldValues {
  if (isOverridden || !config.overrideValueMap) return fieldValues
  let next = fieldValues
  getOverrideFieldsToClear(config).forEach((fieldId) => {
    if (isFieldValueMissing(unwrapFieldValue(next[fieldId]))) return
    if (next === fieldValues) next = { ...fieldValues }
    next[fieldId] = wrapFieldValue(null)
  })
  return next
}

/**
 * Compute is incomplete: null the system boolean (`fieldId`) and, for a mapped
 * section, the published enum (`overrideField`). Field writes merge, so a
 * deleted key leaves the previous result on the ticket instead of clearing it.
 */
function clearIncompleteComputedResult(
  config: ComputedSection,
  fieldValues: FieldValues
): FieldValues {
  let next = fieldValues
  const clearField = (fieldId: string | undefined): void => {
    if (!fieldId) return
    if (isFieldValueMissing(unwrapFieldValue(next[fieldId]))) return
    if (next === fieldValues) next = { ...fieldValues }
    next[fieldId] = wrapFieldValue(null)
  }
  clearField(config.fieldId)
  if (config.overrideValueMap) clearField(config.overrideField)
  return next
}

function persistNormalizedMappedOverride(
  config: ComputedSection,
  fieldValues: FieldValues,
  context: Record<string, unknown> | undefined
): FieldValues {
  const { finalResult, isOverridden } = evaluateComputedSectionWithOverride(
    config,
    fieldValues,
    context
  )
  return persistMappedOverrideField(
    config,
    stripInactiveMappedOverrideMetadata(config, fieldValues, isOverridden),
    finalResult
  )
}

/**
 * Applies or reverts a computed-section override onto ticket field values.
 * Field-value writes are merged, so cleared keys must be persisted as null.
 */
export function applyComputedSectionOverride(
  config: ComputedSection,
  fieldValues: FieldValues,
  decision: {
    outcome?: boolean
    justification?: string
    computedResult: boolean | undefined
    actor: string
    timestamp: string
    parameterSnapshot: string
  }
): FieldValues {
  const updated = { ...fieldValues }
  getOverrideFieldsToClear(config).forEach((fieldId) => {
    updated[fieldId] = wrapFieldValue(null)
  })

  if (decision.outcome === undefined) {
    if (config.overrideValueMap && config.overrideField) {
      updated[config.overrideField] = withFieldOrigin(
        decision.computedResult === undefined
          ? wrapFieldValue(null)
          : wrapFieldValue(
              serializeOverrideValue(config, decision.computedResult)
            ),
        'system'
      )
    }
    return updated
  }

  if (!config.overrideField) return updated

  updated[config.overrideField] = wrapFieldValue(
    serializeOverrideValue(config, decision.outcome)
  )
  if (config.overrideJustificationField && decision.justification) {
    updated[config.overrideJustificationField] = wrapFieldValue(
      decision.justification
    )
  }
  if (config.overrideActorField) {
    updated[config.overrideActorField] = wrapFieldValue(decision.actor)
  }
  if (config.overrideTimestampField) {
    updated[config.overrideTimestampField] = wrapFieldValue(decision.timestamp)
  }
  if (config.overrideParameterSnapshotField) {
    updated[config.overrideParameterSnapshotField] = wrapFieldValue(
      decision.parameterSnapshot
    )
  }
  return updated
}

export function normalizeComputedSectionFieldValues(
  config: ComputedSection,
  fieldValues: FieldValues,
  context?: Record<string, unknown>
): FieldValues {
  let normalized = fieldValues

  if (haveOverrideInputsChanged(config, fieldValues, context)) {
    normalized = { ...fieldValues }
    getOverrideFieldsToClear(config).forEach((fieldId) => {
      normalized[fieldId] = wrapFieldValue(null)
    })
    const computedAfterClear = evaluateComputedSection(
      config,
      normalized,
      context
    )
    normalized =
      computedAfterClear !== undefined
        ? writeMappedOverrideValue(config, normalized, computedAfterClear)
        : unpublishMappedOverrideField(config, normalized)
  }

  if (!config.fieldId) {
    return persistNormalizedMappedOverride(config, normalized, context)
  }

  const { computedResult, finalResult, isOverridden } =
    evaluateComputedSectionWithOverride(config, normalized, context)
  normalized = stripInactiveMappedOverrideMetadata(
    config,
    normalized,
    isOverridden
  )
  const persistedResult = config.overrideValueMap ? computedResult : finalResult
  if (persistedResult === undefined) {
    return clearIncompleteComputedResult(config, normalized)
  }

  if (
    unwrapFieldValue<boolean>(normalized[config.fieldId]) !== persistedResult
  ) {
    normalized = {
      ...normalized,
      [config.fieldId]: wrapFieldValue(persistedResult),
    }
  }

  return persistMappedOverrideField(config, normalized, finalResult)
}

export function resolveParameters(
  config: ComputedSection,
  fieldValues: FieldValues
): Record<string, unknown> {
  return reduce(
    config.functionSpec.parameters,
    (acc, paramSpec) => {
      const value = unwrapFieldValue(fieldValues[paramSpec.field])

      if (value !== undefined && value !== null) {
        acc[paramSpec.field] = value
      } else if (paramSpec.defaultValue !== undefined) {
        acc[paramSpec.field] = paramSpec.defaultValue
      } else {
        acc[paramSpec.field] =
          'type' in paramSpec && paramSpec.type === 'tri-state' ? 'low' : false
      }

      return acc
    },
    {} as Record<string, unknown>
  )
}

export function evaluateComputedSection(
  config: ComputedSection,
  fieldValues: FieldValues,
  context?: Record<string, unknown>
): boolean | undefined {
  if (config.requireAllParameters && !hasParameterValue(config, fieldValues)) {
    return undefined
  }

  const fn = getComputeFunction(config.function)
  if (!fn) return undefined

  const params = resolveParameters(config, fieldValues)
  const computedContext = { functionSpec: config.functionSpec, ...context }

  return fn(params, computedContext)
}

export function evaluateComputedSectionWithOverride(
  config: ComputedSection,
  fieldValues: FieldValues,
  context?: Record<string, unknown>
): ComputedSectionResult {
  const computedResult = evaluateComputedSection(config, fieldValues, context)
  const overrideValue = readLiveOverrideValue(
    config,
    fieldValues,
    computedResult,
    context
  )

  const rawOverrideJustification =
    config.allowOverride && config.overrideJustificationField
      ? unwrapFieldValue<string>(fieldValues[config.overrideJustificationField])
      : undefined
  const overrideJustification =
    typeof rawOverrideJustification === 'string'
      ? rawOverrideJustification
      : undefined

  const rawOverrideActor = config.overrideActorField
    ? unwrapFieldValue<string>(fieldValues[config.overrideActorField])
    : undefined
  const overrideActor =
    typeof rawOverrideActor === 'string' ? rawOverrideActor : undefined

  const rawOverrideTimestamp = config.overrideTimestampField
    ? unwrapFieldValue<string>(fieldValues[config.overrideTimestampField])
    : undefined
  const overrideTimestamp =
    typeof rawOverrideTimestamp === 'string' ? rawOverrideTimestamp : undefined

  const rawOverrideParameterSnapshot = config.overrideParameterSnapshotField
    ? unwrapFieldValue<string>(
        fieldValues[config.overrideParameterSnapshotField]
      )
    : undefined
  const overrideParameterSnapshot =
    typeof rawOverrideParameterSnapshot === 'string'
      ? rawOverrideParameterSnapshot
      : undefined

  const isOverridden = overrideValue !== undefined
  const finalResult =
    computedResult === undefined
      ? undefined
      : isOverridden
        ? overrideValue
        : computedResult

  return {
    computedResult,
    isOverridden,
    overrideValue,
    finalResult,
    overrideJustification: isOverridden ? overrideJustification : undefined,
    overrideActor: isOverridden ? overrideActor : undefined,
    overrideTimestamp: isOverridden ? overrideTimestamp : undefined,
    overrideParameterSnapshot: isOverridden
      ? overrideParameterSnapshot
      : undefined,
  }
}
